<template>
  <div>
    <Modal modalId="remove-apparatuses-selection-modal" size="modal-lg" @hidden="closeRemovalModals()">
      <ModalHeader :title="$t('xSelectProductsToRemove')" icon="far fa-badge-dollar colorOrange mr-1" />
      <ModalBody>
        <div role="alert" v-if="showRemoveError" class="alert alert-danger">
          <p class="mb-2">
            <i class="far fa-circle-exclamation mr-1"></i>
            {{ $t('xSomethingWentWrong') }}
          </p>
          <p class="ml-4">
            {{ $t('xPleaseWaitAndTryAgain') }}
          </p>
        </div>

        <div class="col mb-4">
          <p>
            <b> {{ $t('xSelectProductsToRemoveWarningText1') }} </b>
            {{ ' ' }}
            {{ $t('xSelectProductsToRemoveWarningText2') }}
          </p>
        </div>

        <div v-if="subscriptionGenerators && subscriptionGenerators.length > 0" class="mb-4">
          <div class="col d-flex align-items-center mb-2">
            <b-img :src="require('@/assets/images/generator.svg')" class="mr-2" />
            <strong> {{ $t('xGenerators') }} </strong>
          </div>
          <div class="col">
            <div
              v-for="generator in subscriptionGenerators"
              :key="generator.id"
              class="custom-control custom-checkbox mb-3"
            >
              <input
                :id="generator.id"
                :value="generator"
                type="checkbox"
                class="custom-control-input"
                v-model="apparatusesToRemove"
                :class="checkboxStatus($v.apparatusesToRemove)"
              />
              <label class="custom-control-label" :for="generator.id">
                {{ generator.name }}
              </label>
            </div>
          </div>
        </div>

        <div v-if="subscriptionTanks && subscriptionTanks.length > 0" class="mb-4">
          <div class="col d-flex align-items-center mb-2">
            <b-img :src="require('@/assets/images/tank.svg')" class="mr-2" />
            <strong> {{ $t('xFuelMonitors') }} </strong>
          </div>
          <div class="col">
            <div v-for="tank in subscriptionTanks" :key="tank.id" class="custom-control custom-checkbox mb-3">
              <input
                :id="tank.id"
                :value="tank"
                type="checkbox"
                class="custom-control-input"
                v-model="apparatusesToRemove"
                :class="checkboxStatus($v.apparatusesToRemove)"
              />
              <label class="custom-control-label" :for="tank.id">
                {{ tank.name }}
              </label>
            </div>
          </div>
        </div>

        <div v-if="showErrorMessage">
          <div class="col">
            <p class="text-small colorRed">{{ errorMessageText }}</p>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <button class="btn btn-outline-warning" @click="closeRemovalModals()">
          {{ $t('xCancel') }}
        </button>
        <button class="btn btn-warning" @click="removeSelectedApparatuses()">
          {{ $t('xRemoveProductsButtonText') }}
        </button>
      </ModalFooter>
    </Modal>

    <Modal modalId="remove-products-modal" size="modal-md" @hidden="closeRemovalModals()">
      <ModalHeader :title="$t('xRemoveProductsButtonText')" icon="fa-light fa-triangle-exclamation mr-1">
        <div class="cols-auto">
          <button type="button" class="btn btn-icon-button btn-icon-button--gray" @click="closeRemovalModals()">
            <i class="fas fa-times" />
          </button>
        </div>
      </ModalHeader>
      <ModalBody>
        <div class="mb-4">
          <p>{{ $t('xRemoveProductsLoseAccess') }}</p>
        </div>

        <ul class="mb-4 ml-n3">
          <li v-for="apparatus in apparatusesToRemove" :key="apparatus.id" class="mb-1">
            {{ apparatus.name }}
          </li>
        </ul>

        <div>
          <p>{{ $t('xRemoveProductsConfirmation') }}</p>
        </div>
      </ModalBody>
      <ModalFooter>
        <button class="btn btn-outline-warning flex flex-grow-1" @click="closeRemovalModals()">
          {{ $t('xNoCancelButtonText') }}
        </button>
        <button class="btn btn-warning flex flex-grow-1" @click="removeProducts()">
          {{ $t('xYesRemoveProductsButtonText') }}
        </button>
      </ModalFooter>
    </Modal>

    <Modal modalId="removing-products-spinner-modal" size="modal-lg">
      <ModalBody style="padding-top: 100px; padding-bottom: 100px;">
        <div class="d-flex justify-content-center align-items-center">
          <b-spinner style="width: 10rem; height: 10rem;" variant="warning"></b-spinner>
        </div>
      </ModalBody>
    </Modal>
  </div>
</template>

<script>
import * as Constants from '@/constants'
import { required } from 'vuelidate/lib/validators'

export default {
  props: ['subscription', 'accountProfile'],
  data() {
    return {
      apparatusesToRemove: [],
      showErrorMessage: false,
      errorMessageText: '',
      showRemoveError: false
    }
  },
  validations: {
    apparatusesToRemove: {
      required,
      $lazy: true,
      validateCheckbox(apparatusesToRemove) {
        if (apparatusesToRemove.length === 0) {
          this.throwCheckboxErrors(this.$t('xSelectOneProductErrorText'))
        } else if (apparatusesToRemove.length === this.subscriptionGenerators.length + this.subscriptionTanks.length) {
          this.throwCheckboxErrors(this.$t('xAllProductsSelectedErrorText'))
        } else {
          this.resetCheckboxErrors()
          return true
        }
        return false
      }
    }
  },
  methods: {
    closeRemovalModals() {
      this.$v.$reset()
      this.apparatusesToRemove = []
      this.showErrorMessage = false
      this.errorMessageText = ''
      this.closeModal('remove-apparatuses-selection-modal')
      this.closeModal('remove-products-modal')
      this.closeModal('removing-products-spinner-modal')
    },
    checkboxStatus(validation) {
      return {
        'is-invalid': validation.$error
      }
    },
    resetCheckboxErrors() {
      this.$v.$reset()
      this.showErrorMessage = false
      this.errorMessageText = ''
      this.showRemoveError = false
    },
    throwCheckboxErrors(errorText) {
      this.showErrorMessage = true
      this.errorMessageText = errorText
    },
    removeSelectedApparatuses() {
      this.resetCheckboxErrors()

      this.$v.$touch()
      if (!this.$v.apparatusesToRemove.$error) {
        this.closeModal('remove-apparatuses-selection-modal')
        this.openModal('remove-products-modal')
      }
    },
    removeFailed() {
      this.showRemoveError = true
      this.closeModal('remove-products-modal')
      this.openModal('remove-apparatuses-selection-modal')
      this.closeModal('removing-products-spinner-modal')
    },
    async removeProducts() {
      this.closeModal('remove-products-modal')
      this.openModal('removing-products-spinner-modal')
      let subscriptionApparatusList = this.subscription.apparatuses
        .filter(
          subscriptionApparatus =>
            !this.apparatusesToRemove.some(apparatus => apparatus.id === subscriptionApparatus.id)
        )
        .map(apparatusToKeep => {
          return apparatusToKeep.id
        })
      await this.$store
        .dispatch('updateSubscriptionApparatuses', {
          organizationId: this.accountProfile.organizationId,
          store: this.subscription.subscriptionStore,
          productId: this.subscription.subscriptionProductId,
          apparatusIds: subscriptionApparatusList
        })
        .then(async () => {
          // If the plan is pending change, need to update both the current plan and the pending plan
          if (this.subscription.subscriptionStatus === Constants.SubscriptionDetailPendingProductChange) {
            await this.$store
              .dispatch('updateSubscriptionApparatuses', {
                organizationId: this.accountProfile.organizationId,
                store: this.subscription.subscriptionStore,
                productId: this.subscription.subscriptionPendingProductId,
                apparatusIds: subscriptionApparatusList
              })
              .catch(() => {
                this.removeFailed()
              })
          }
          for (const apparatusToRemove of this.apparatusesToRemove) {
            await this.$store
              .dispatch('removeApparatus', {
                organizationId: this.accountProfile.organizationId,
                apparatusId: apparatusToRemove.id
              })
              .catch(() => {
                this.removeFailed()
              })
          }
          if (!this.showRemoveError) {
            await this.$store
              .dispatch('getSubscriptionDetails', this.accountProfile.organizationId)
              .then(response => {
                this.closeRemovalModals()
                let removedApparatusPlan = response.data?.subscriptions?.find(
                  subscription =>
                    subscription.store !== Object.values(Constants.SubscriptionStoreTypes).indexOf('Dealer')
                )
                this.$store.dispatch('displayToast', {
                  title: this.planEligibleForDowngrade(removedApparatusPlan)
                    ? this.$t('xProductsRemovedSuccessfully')
                    : this.$t('xSuccess'),
                  message: this.planEligibleForDowngrade(removedApparatusPlan)
                    ? this.$t('xYourPlanEligibleForDowngrade')
                    : this.$t('xProductsRemovedSuccessfully'),
                  variant: 'success'
                })

                setTimeout(() => {
                  window.location.reload()
                }, 3000)
              })
              .catch(() => {
                this.removeFailed()
              })
          }
        })
        .catch(() => {
          this.removeFailed()
        })
    },
    filterApparatusByType(type) {
      return this.subscription?.apparatuses?.filter(apparatus => apparatus.type === type) || []
    }
  },
  computed: {
    subscriptionGenerators() {
      return this.filterApparatusByType(Constants.HsbApparatusType)
    },
    subscriptionTanks() {
      return this.filterApparatusByType(Constants.FuelTankApparatusType)
    }
  }
}
</script>

<style scoped lang="scss">
.show-checkbox-error {
  background-color: var(--red-600);
  border-color: rgba(var(--red-600), 0.5);
}
</style>
